import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { useMemo } from "react";
import { mockAuthContext } from "../../tests/mock.data";

export interface AuthenticatedUser {
  isAuthenticated: boolean;
  isLoading: boolean;
  roles: string[];
  groups: string[];
  account: any;
  hasRole: (requiredRoles: string[]) => boolean;
  hasGroup: (requiredGroups: string[]) => boolean;
}

export function useAuthenticatedUser(): AuthenticatedUser {
  if (process.env.NEXT_PUBLIC_E2E) {
    return {
      isAuthenticated: true,
      isLoading: false,
      roles: mockAuthContext.roles,
      groups: mockAuthContext.groups,
      account: { username: mockAuthContext.user.username },
      hasRole: mockAuthContext.hasRole,
      hasGroup: mockAuthContext.hasGroup,
    };
  }

  const isAuthenticated = useIsAuthenticated();
  const { accounts, inProgress } = useMsal();

  const userData = useMemo(() => {
    if (!isAuthenticated || !accounts || accounts.length === 0) {
      return {
        isAuthenticated: false,
        isLoading: inProgress !== InteractionStatus.None,
        roles: [],
        groups: [],
        account: null,
        hasRole: () => false,
        hasGroup: () => false,
      };
    }

    const account = accounts[0];
    const claims = account?.idTokenClaims as any;
    const roles = claims?.roles || [];
    const groups = claims?.groups || [];

    // Check if roles are still loading
    const rolesReady = !!(accounts && accounts.length > 0 && claims);
    const isLoading = inProgress !== InteractionStatus.None || !rolesReady;

    const hasRole = (requiredRoles: string[]): boolean => {
      if (!requiredRoles || requiredRoles.length === 0) {
        return true;
      }
      return requiredRoles.some(role => roles.includes(role));
    };

    const hasGroup = (requiredGroups: string[]): boolean => {
      if (!requiredGroups || requiredGroups.length === 0) {
        return true;
      }
      return requiredGroups.some(group => groups.includes(group));
    };

    return {
      isAuthenticated,
      isLoading,
      roles,
      groups,
      account,
      hasRole,
      hasGroup,
    };
  }, [isAuthenticated, accounts, inProgress]);

  return userData;
}
